import type { HTMLAttributes } from 'react';

import { cn } from '../lib/cn';

export type CardVariant = 'default' | 'raised' | 'zone';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** `default` — level-1 surface · `raised` — level-2 elevated · `zone` — dark accent surface. */
  variant?: CardVariant;
  /** Lifts the card on hover; use for clickable cards. */
  interactive?: boolean;
}

const variants: Record<CardVariant, string> = {
  default:
    'border border-border bg-surface shadow-card transition-all duration-300 ease-out hover:border-accent-200 hover:shadow-glow-accent',
  raised: 'border border-border bg-surface shadow-lift transition-all duration-300 ease-out',
  zone: 'ts-zone shadow-zone transition-all duration-300 ease-out',
};

export function Card({ variant = 'default', interactive = false, className, ...rest }: CardProps) {
  return (
    <div
      className={cn(
        'rounded-2xl',
        variants[variant],
        interactive && 'cursor-pointer hover:-translate-y-1',
        className,
      )}
      {...rest}
    />
  );
}

export function CardHeader({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'border-border flex items-center justify-between gap-3 border-b px-6 py-4',
        className,
      )}
      {...rest}
    />
  );
}

export function CardTitle({ className, ...rest }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={cn('text-ink text-[15px] font-semibold tracking-tight', className)} {...rest} />
  );
}

export function CardBody({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-6 py-5', className)} {...rest} />;
}

export function CardFooter({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('border-border border-t px-6 py-4', className)} {...rest} />;
}
